import {
  boundingBoxToTuple,
  createElementFactory,
  getValueAtPath,
  JsxgraphBaseWidget,
  type JsxgraphStatic,
  type JsxgraphThemeService,
  WidgetEvent,
  type WidgetState,
} from '@examdojo/core/widget';
import type { CreatedElement, ElementContext } from './elements/element.model';
import { isParameterizedElement } from './elements/element.model';
import { ElementResolver } from './elements/element.resolver';
import { DEFAULT_FEATURE_THEME, FeatureRegistry } from './elements/features';
import { FunctionRegistry } from './elements/functions/function.registry';
import type { GraphpadParams, GraphpadWidgetConfig } from './graphpad.model';

/**
 * GraphpadWidget is a declarative graph system that interprets config to create
 * functions, features, handles, derived elements, and relationships.
 *
 * Unlike per-function-type widgets, this single widget can render any combination
 * of supported elements based on the declarative config.
 */
export class GraphpadWidget extends JsxgraphBaseWidget<GraphpadWidgetConfig> {
  constructor(container: HTMLElement, jsxInstance: JsxgraphStatic, themeService: JsxgraphThemeService) {
    super(container, jsxInstance, themeService);
    this.id = `graphpad-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    // Initialize resolver and register all creators
    this.resolver = new ElementResolver();
    this.registerCreators();
  }

  readonly id: string;
  readonly type = 'graphpad';

  private config?: GraphpadWidgetConfig;
  private readonly createdElements: Map<string, CreatedElement> = new Map();
  private readonly resolver: ElementResolver;

  protected getCurrentConfig(): GraphpadWidgetConfig | undefined {
    return this.config;
  }

  getState(): WidgetState {
    return {
      type: this.type,
      id: this.id,
      params: this.config?.params,
    };
  }

  init(config: GraphpadWidgetConfig): void {
    this.config = config;
    const params = config.params;
    const displayConfig = config.display;

    // Validate config - check for multiple handles on same function
    this.validateHandleConfig(params);

    // Get coordinate system config
    const coordinateSystem = displayConfig?.coordinateSystem;
    const boundingbox = boundingBoxToTuple(
      coordinateSystem?.boundingbox ?? { xStart: -10, xEnd: 10, yStart: -10, yEnd: 10 },
    );

    // Initialize board
    this.initBoard({
      boundingbox,
      axis: false,
    });

    // Create coordinate system from display config
    this.createCoordinateSystem(coordinateSystem);

    // Create element factory for styled element creation
    const elementFactory = createElementFactory(this.board, this.container, this.themeService);

    // Create element context with factory and feature theme
    const context: ElementContext = {
      board: this.board,
      elements: this.createdElements,
      getParam: (path) => this.getParamValue(path, params),
      themeService: this.themeService,
      elementFactory,
      featureTheme: DEFAULT_FEATURE_THEME,
      container: this.container,
      onParamChange: (elementId, changedParams) => {
        this.emit(WidgetEvent.ParamChange, { elementId, params: changedParams });
      },
    };

    // Resolve and create elements in dependency order
    const sortedIds = this.resolver.resolveDependencies(params);

    for (const elementId of sortedIds) {
      const def = params[elementId];
      const elementDisplay = displayConfig?.elements?.[elementId];

      try {
        const created = this.resolver.createElement(elementId, def, elementDisplay, context);
        this.createdElements.set(elementId, created);

        // Register parameterized elements for UPDATE_PARAM support (enables animated parameter updates)
        // This includes functions, features (tangent, secant), and any element with getParams/setParams
        if (isParameterizedElement(created)) {
          this.registerParameterizedElement(elementId, created);
        }

        // Track JSXGraph objects in the base class objects map
        created.objects.forEach((obj, idx) => {
          this.objects.set(`${elementId}-${idx}`, obj);
        });
      } catch (error) {
        console.error(`Failed to create element '${elementId}':`, error);
      }
    }
  }

  getElement(elementId: string): CreatedElement | undefined {
    return this.createdElements.get(elementId);
  }

  override destroy(): void {
    // Destroy created elements in reverse order
    const ids = Array.from(this.createdElements.keys()).reverse();
    ids.forEach((id) => {
      const element = this.createdElements.get(id);
      if (element) {
        try {
          element.destroy();
        } catch (error) {
          console.error(`Failed to destroy element '${id}':`, error);
        }
      }
    });
    this.createdElements.clear();

    super.destroy();
  }

  private registerCreators(): void {
    // Register function creators
    this.resolver.registerCreators(FunctionRegistry.getAll());

    // Register feature creators (includes derivative, intersection, roots, extrema, tangent, secant, handle)
    // Set handle drag callback to emit param changes before registering
    FeatureRegistry.setHandleDragCallback((elementId, params) => {
      this.emit(WidgetEvent.ParamChange, { elementId, params });
    });
    this.resolver.registerCreators(FeatureRegistry.getAll());
  }

  /**
   * Validate handle configuration at runtime.
   *
   * Note: When using GraphpadConfigBuilder, this constraint is enforced at compile-time.
   * This runtime check serves as a safety net for configs built manually or from JSON.
   *
   * Currently, only one handle per function is supported.
   */
  private validateHandleConfig(params: GraphpadParams): void {
    const handlesByFunction = new Map<string, string[]>();

    for (const [elementId, def] of Object.entries(params)) {
      if (def.type === 'handle') {
        const handleParams = def.params as { of: string; behavior: string } | undefined;
        const functionId = handleParams?.of;

        if (!functionId) {
          throw new Error(`Handle '${elementId}' is missing params.of`);
        }

        if (!handlesByFunction.has(functionId)) {
          handlesByFunction.set(functionId, []);
        }
        handlesByFunction.get(functionId)!.push(elementId);
      }
    }

    for (const [functionId, handleIds] of handlesByFunction) {
      if (handleIds.length > 1) {
        throw new Error(
          `Multiple handles on the same function are not supported. ` +
            `Function '${functionId}' has ${handleIds.length} handles: [${handleIds.join(', ')}]. ` +
            `Please use only one handle per function.`,
        );
      }
    }
  }

  /**
   * Get a parameter value from a dot-notation path.
   * Supports paths like 'f.params.a' (element ID + property path)
   */
  private getParamValue(path: string, params: GraphpadParams): number {
    const value = getValueAtPath(params, path);
    return typeof value === 'number' ? value : 0;
  }
}
